import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { flows } from '@repletoryreach/sdk-api';
import { Card, Stack, Title, Text, Group, Button } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../../i18n/I18nProvider';
import { DeleteFlowDialog, confirmSwitchOff } from '../../components/FlowGuards';

type Flow = NonNullable<Awaited<ReturnType<typeof flows.getFlow>>['data']>;

const MUTED = 'var(--rr-color-text-muted, #94a3b8)';

export function LifecycleTab({ flow }: { flow: Flow }) {
  const t = useT();
  const qc = useQueryClient();
  const nav = useNavigate();

  const toggleActive = useMutation({
    mutationFn: () => flows.updateFlow(flow.id, { is_active: !flow.is_active }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['flow', flow.id] });
      qc.invalidateQueries({ queryKey: ['flows'] });
      notifySuccess(t(flow.is_active ? 'flows.view.lifecycle.toast.deactivated' : 'flows.view.lifecycle.toast.activated'), t('toast.saved'));
    },
    onError: (err) =>
      notifyError(err instanceof Error ? err.message : t('flows.view.lifecycle.updateFailed'), t('flows.builder.saveFailed'))
  });

  // Switching off and deleting name the switched-on flows that open this one first (2 November 2026).
  const [checking, setChecking] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const toggle = async () => {
    if (!flow.is_active) { toggleActive.mutate(); return; }
    setChecking(true);
    try {
      await confirmSwitchOff(flow, t, () => toggleActive.mutate());
    } finally {
      setChecking(false);
    }
  };

  return (
    <Stack gap="md">
      <Card withBorder>
        <Stack gap="sm">
          <Title order={4}>{t('flows.view.lifecycle.activation')}</Title>
          <Text fz="sm" c={MUTED}>
            {flow.is_active
              ? t('flows.view.lifecycle.activeHint')
              : t('flows.view.lifecycle.inactiveHint')}
          </Text>
          <Group>
            <Button
              size="xs"
              variant="outline"
              color={flow.is_active ? 'red' : 'green'}
              loading={toggleActive.isPending || checking}
              onClick={() => void toggle()}
            >
              {flow.is_active ? t('flows.view.lifecycle.deactivateFlow') : t('flows.view.lifecycle.activateFlow')}
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder style={{ borderColor: 'var(--rr-color-danger)' }}>
        <Stack gap="sm">
          <Title order={4} c="red">{t('flows.modal.delete.confirm')}</Title>
          <Alert color="red" variant="light">
            {t('flows.view.lifecycle.deleteHint')}
          </Alert>
          <Group>
            <Button
              size="xs"
              color="red"
              variant="outline"
              onClick={() => setDeleting(true)}
            >
              {t('flows.actions.deleteEllipsis')}
            </Button>
          </Group>
        </Stack>
      </Card>

      <DeleteFlowDialog flow={deleting ? flow : null} onClose={() => setDeleting(false)} onDeleted={() => nav('/flows')} />
    </Stack>
  );
}
